// Environment Configuration Management

export interface EnvironmentConfig {
  api: {
    baseUrl: string;
    version: string;
    timeout: number;
  };
  auth: {
    tokenKey: string;
    refreshTokenKey: string;
    tokenExpiry: number;
  };
  app: {
    name: string;
    version: string;
    environment: string;
    debug: boolean;
  };
  features: {
    realTimeUpdates: boolean;
    bulkOperations: boolean;
    exportFunctionality: boolean;
    analytics: boolean;
    darkMode: boolean;
  };
  upload: {
    maxFileSize: number;
    allowedTypes: string[];
    endpoint: string;
  };
  security: {
    enableCSRF: boolean;
    sessionTimeout: number;
    enableEncryption: boolean;
  };
}

const developmentConfig: EnvironmentConfig = {
  api: {
    baseUrl: process.env.REACT_APP_API_BASE_URL || "https://agora.paddi.ng/api",
    version: process.env.REACT_APP_API_VERSION || "v1",
    timeout: 10000,
  },
  auth: {
    tokenKey: "token",
    refreshTokenKey: "veggie_refresh_token",
    tokenExpiry: 24 * 60 * 60 * 1000, // 24 hours
  },
  app: {
    name: "Veggie Supply Chain",
    version: "1.0.0",
    environment: "development",
    debug: true,
  },
  features: {
    realTimeUpdates: false, // Disabled in development
    bulkOperations: true,
    exportFunctionality: true,
    analytics: false,
    darkMode: true,
  },
  upload: {
    maxFileSize: 10 * 1024 * 1024, // 10MB
    allowedTypes: [
      ".pdf",
      ".jpg",
      ".jpeg",
      ".png",
      ".doc",
      ".docx",
      ".xls",
      ".xlsx",
    ],
    endpoint: "/upload",
  },
  security: {
    enableCSRF: false, // Disabled in development
    sessionTimeout: 60 * 60 * 1000, // 1 hour
    enableEncryption: false,
  },
};

const productionConfig: EnvironmentConfig = {
  api: {
    baseUrl:
      process.env.REACT_APP_API_BASE_URL ||
      "https://api.veggieworld.africa/api",
    version: process.env.REACT_APP_API_VERSION || "v1",
    timeout: 15000,
  },
  auth: {
    tokenKey: "token",
    refreshTokenKey: "veggie_refresh_token",
    tokenExpiry: 24 * 60 * 60 * 1000, // 24 hours
  },
  app: {
    name: "Veggie Supply Chain",
    version: "1.0.0",
    environment: "production",
    debug: false,
  },
  features: {
    realTimeUpdates: true,
    bulkOperations: true,
    exportFunctionality: true,
    analytics: true,
    darkMode: true,
  },
  upload: {
    maxFileSize: 10 * 1024 * 1024, // 10MB
    allowedTypes: [
      ".pdf",
      ".jpg",
      ".jpeg",
      ".png",
      ".doc",
      ".docx",
      ".xls",
      ".xlsx",
    ],
    endpoint: "/upload",
  },
  security: {
    enableCSRF: true,
    sessionTimeout: 30 * 60 * 1000, // 30 minutes
    enableEncryption: true,
  },
};

const testConfig: EnvironmentConfig = {
  ...developmentConfig,
  app: {
    ...developmentConfig.app,
    environment: "test",
  },
  api: {
    ...developmentConfig.api,
    baseUrl: "https://agora.paddi.ng/api", // Test server
  },
};

function getEnvironment(): string {
  return process.env.NODE_ENV || "development";
}

export function getConfig(): EnvironmentConfig {
  const env = getEnvironment();
  console.log("Environment:", env);

  let selectedConfig: EnvironmentConfig;
  switch (env) {
    case "production":
      selectedConfig = productionConfig;
      break;
    case "test":
      selectedConfig = testConfig;
      break;
    case "development":
    default:
      selectedConfig = developmentConfig;
      break;
  }

  console.log("API Config:", {
    baseUrl: selectedConfig.api.baseUrl,
    version: selectedConfig.api.version,
    fullUrl: selectedConfig.api.baseUrl + "/" + selectedConfig.api.version,
  });

  return selectedConfig;
}

export const config = getConfig();

// Helper functions for common config access
export const isDevelopment = () => config.app.environment === "development";
export const isProduction = () => config.app.environment === "production";
export const isDebugMode = () => config.app.debug;

export default config;
